import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Box,
  IconButton,
  Menu,
  MenuItem,
  useScrollTrigger,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { motion } from "framer-motion";
import { MotionBox, hoverScale } from "@rusha/shared-theme";

const MotionButton = motion.create(Button);

const Navbar: React.FC = () => {
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);

  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 50,
  });

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  return (
    <AppBar
      position="fixed"
      color="transparent"
      elevation={trigger ? 4 : 0}
      sx={{
        backgroundColor: trigger ? "background.paper" : "transparent",
        transition: "all 0.3s ease",
        py: trigger ? 0.75 : 1,
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters>
          <Box
            component="a"
            href="/"
            sx={{
              flexGrow: 1,
              color: "primary.main",
              fontWeight: 700,
              fontSize: "1.5rem",
              display: { xs: "none", md: "flex" },
              textDecoration: "none",
            }}
            aria-label="Rusha home"
          >
            Rusha
          </Box>

          <Box sx={{ display: { xs: "flex", md: "none" } }}>
            <IconButton
              size="large"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
              aria-label="Open navigation menu"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              keepMounted
              transformOrigin={{ vertical: "top", horizontal: "left" }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{ display: { xs: "block", md: "none" } }}
            >
              <MenuItem
                component="a"
                href="/#how-it-works"
                onClick={handleCloseNavMenu}
              >
                <Typography textAlign="center">How It Works</Typography>
              </MenuItem>
              <MenuItem
                component="a"
                href="/#platform"
                onClick={handleCloseNavMenu}
              >
                <Typography textAlign="center">Platform</Typography>
              </MenuItem>
              <MenuItem
                component="a"
                href="/templates"
                onClick={handleCloseNavMenu}
              >
                <Typography textAlign="center">Templates</Typography>
              </MenuItem>
              <MenuItem component="a" href="/docs" onClick={handleCloseNavMenu}>
                <Typography textAlign="center">Docs</Typography>
              </MenuItem>
              <MenuItem
                component="a"
                href="/#pricing"
                onClick={handleCloseNavMenu}
              >
                <Typography textAlign="center">Pricing</Typography>
              </MenuItem>
              <MenuItem
                component="a"
                href="https://app.rusha.tierravivaai.net"
                onClick={handleCloseNavMenu}
              >
                <Typography textAlign="center">Get Started</Typography>
              </MenuItem>
            </Menu>
          </Box>

          <Box
            component="a"
            href="/"
            sx={{
              flexGrow: 1,
              color: "primary.main",
              fontWeight: 700,
              fontSize: "1.5rem",
              display: { xs: "flex", md: "none" },
              textDecoration: "none",
            }}
            aria-label="Rusha home"
          >
            Rusha
          </Box>

          <nav aria-label="Main navigation">
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                gap: 3,
                alignItems: "center",
              }}
            >
              <MotionBox {...hoverScale.scaleSm}>
                <Button
                  href="/#how-it-works"
                  color="inherit"
                  sx={{ fontWeight: 500 }}
                >
                  How It Works
                </Button>
              </MotionBox>
              <MotionBox {...hoverScale.scaleSm}>
                <Button
                  href="/#platform"
                  color="inherit"
                  sx={{ fontWeight: 500 }}
                >
                  Platform
                </Button>
              </MotionBox>
              <MotionBox {...hoverScale.scaleSm}>
                <Button
                  href="/templates"
                  color="inherit"
                  sx={{ fontWeight: 500 }}
                >
                  Templates
                </Button>
              </MotionBox>
              <MotionBox {...hoverScale.scaleSm}>
                <Button href="/docs" color="inherit" sx={{ fontWeight: 500 }}>
                  Docs
                </Button>
              </MotionBox>
              <MotionBox {...hoverScale.scaleSm}>
                <Button
                  href="/#pricing"
                  color="inherit"
                  sx={{ fontWeight: 500 }}
                >
                  Pricing
                </Button>
              </MotionBox>

              <MotionButton
                href="https://app.rusha.tierravivaai.net"
                variant="contained"
                color="primary"
                {...hoverScale.scaleLg}
              >
                Get Started
              </MotionButton>
            </Box>
          </nav>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Navbar;
